"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  getOpenworkDemoState,
  completeOpenworkIntro as apiCompleteIntro,
  markOpenworkNotificationActioned as apiMarkActioned,
  markOpenworkNotificationRead as apiMarkRead,
  type OpenworkDemoNotificationDto,
  type OpenworkDemoStateDto,
} from "@/services/api/agentic-v2";
import { clearOpenworkAgentContext } from "@/services/inAppAgent/openworkAgentContextBridge";

export type OpenworkDemoNotificationKind = "info" | "alert" | "success";

/**
 * Per-user OpenWork demo state (notifications + welcome intro).
 *
 * Server-owned — the browser only mirrors the API response. Home, Ani, and any
 * other surface derive from this so refresh / second tab / reseed never disagree.
 */
export type OpenworkDemoNotification = {
  id: string;
  kind: OpenworkDemoNotificationKind;
  title: string;
  body: string;
  taskScenarioRef?: string;
  createdAt: string;
  read: boolean;
  actioned?: boolean;
  actionedAt?: string;
};

type OpenworkDemoState = {
  notifications: OpenworkDemoNotification[];
  introCompleted: boolean;
  loaded: boolean;
};

const EMPTY_STATE: OpenworkDemoState = {
  notifications: [],
  introCompleted: false,
  loaded: false,
};

let cachedState: OpenworkDemoState = EMPTY_STATE;
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let enabled = false;
let configuredUserKey = "";

function notify() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function fromDto(dto: OpenworkDemoStateDto): OpenworkDemoState {
  return {
    loaded: true,
    introCompleted: Boolean(dto.intro_completed),
    notifications: (dto.notifications ?? []).map(fromNotificationDto),
  };
}

function fromNotificationDto(row: OpenworkDemoNotificationDto): OpenworkDemoNotification {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body,
    taskScenarioRef: row.task_scenario_ref ?? undefined,
    createdAt: row.created_at ?? new Date().toISOString(),
    read: Boolean(row.read),
    actioned: Boolean(row.actioned),
    actionedAt: row.actioned_at ?? undefined,
  };
}

async function refresh(): Promise<void> {
  if (!enabled) return;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const dto = await getOpenworkDemoState();
      cachedState = fromDto(dto);
      notify();
    } catch (error) {
      console.warn("[openwork-demo] failed to load demo state", error);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Enable server-backed loading for the current user (called from OpenWork surfaces). */
export function configureOpenworkDemoStorage(userKey: string | null | undefined) {
  const nextKey = userKey?.trim() ?? "";
  const nextEnabled = Boolean(nextKey);
  configuredUserKey = nextKey;
  if (nextEnabled === enabled) {
    if (nextEnabled) void refresh();
    return;
  }
  enabled = nextEnabled;
  if (!enabled) {
    cachedState = EMPTY_STATE;
    notify();
    return;
  }
  cachedState = EMPTY_STATE;
  notify();
  void refresh();
}

let reseedingInFlight = false;
let reseedConfirmOpen = false;

export function setOpenworkReseeding(active: boolean) {
  reseedingInFlight = active;
  notify();
}

export function readOpenworkReseeding(): boolean {
  return reseedingInFlight;
}

export function openOpenworkReseedConfirm() {
  reseedConfirmOpen = true;
  notify();
}

export function closeOpenworkReseedConfirm() {
  reseedConfirmOpen = false;
  notify();
}

export function readOpenworkReseedConfirmOpen(): boolean {
  return reseedConfirmOpen;
}

export function readOpenworkDemoState(): OpenworkDemoState {
  return cachedState;
}

/** Called after reseed — server has already reset the doc; re-fetch. */
export function clearOpenworkDemoState(): Promise<void> {
  if (typeof window !== "undefined" && configuredUserKey) {
    window.localStorage.removeItem(`openwork-demo-state:${configuredUserKey}`);
    window.localStorage.removeItem(`workspace-welcome:${configuredUserKey}`);
  }
  clearOpenworkAgentContext();
  // Reseed already reset server state — show welcome immediately even if /state is slow.
  cachedState = { notifications: [], introCompleted: false, loaded: true };
  notify();
  if (enabled) return refresh();
  return Promise.resolve();
}

export async function completeOpenworkIntro(): Promise<void> {
  cachedState = { ...cachedState, introCompleted: true };
  notify();
  try {
    const dto = await apiCompleteIntro();
    cachedState = fromDto(dto);
    notify();
  } catch (error) {
    console.warn("[openwork-demo] complete-intro failed", error);
    void refresh();
  }
}

export async function markOpenworkNotificationRead(notificationId: string): Promise<void> {
  cachedState = {
    ...cachedState,
    notifications: cachedState.notifications.map((row) =>
      row.id === notificationId ? { ...row, read: true } : row,
    ),
  };
  notify();
  try {
    const dto = await apiMarkRead(notificationId);
    cachedState = fromDto(dto);
    notify();
  } catch (error) {
    console.warn("[openwork-demo] mark-read failed", error);
    void refresh();
  }
}

export async function markOpenworkNotificationActioned(taskScenarioRef: string): Promise<void> {
  const ref = taskScenarioRef.trim();
  if (!ref) return;
  cachedState = {
    ...cachedState,
    notifications: cachedState.notifications.map((row) =>
      row.taskScenarioRef === ref
        ? {
            ...row,
            read: true,
            actioned: true,
            actionedAt: row.actionedAt ?? new Date().toISOString(),
          }
        : row,
    ),
  };
  notify();
  try {
    const dto = await apiMarkActioned(ref);
    cachedState = fromDto(dto);
    notify();
  } catch (error) {
    console.warn("[openwork-demo] mark-actioned failed", error);
    void refresh();
  }
}

export async function refreshOpenworkDemoState(): Promise<void> {
  await refresh();
}

export const OPENWORK_HOME_REFRESH_EVENT = "openwork-home-refresh";

export type OpenworkHomeRefreshDetail = {
  taskId?: string;
};

export function dispatchOpenworkHomeRefresh(detail?: OpenworkHomeRefreshDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPENWORK_HOME_REFRESH_EVENT, { detail }));
  void refresh();
}

const DEMO_HOME_POLL_INTERVAL_MS = 500;
const DEMO_HOME_POLL_DURATION_MS = 12_000;

/** Poll the Home surface snapshot after demo seeds — worker gates can lag Leads stats. */
export function scheduleDemoHomeRefreshPoll(
  refresh: () => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  refresh();
  const started = Date.now();
  const timer = window.setInterval(() => {
    refresh();
    if (Date.now() - started >= DEMO_HOME_POLL_DURATION_MS) {
      window.clearInterval(timer);
    }
  }, DEMO_HOME_POLL_INTERVAL_MS);
  return () => window.clearInterval(timer);
}

export function useOpenworkDemoState() {
  const state = useSyncExternalStore(subscribe, readOpenworkDemoState, () => EMPTY_STATE);
  useEffect(() => {
    if (enabled && !state.loaded) {
      void refresh();
    }
  }, [state.loaded]);
  return state;
}

export function useOpenworkReseeding() {
  return useSyncExternalStore(subscribe, readOpenworkReseeding, () => false);
}

export function useOpenworkReseedConfirmOpen() {
  return useSyncExternalStore(subscribe, readOpenworkReseedConfirmOpen, () => false);
}

export function useOpenworkDemoActions() {
  const markRead = useCallback(async (notificationId: string) => {
    await markOpenworkNotificationRead(notificationId);
  }, []);

  const reset = useCallback(() => {
    void clearOpenworkDemoState();
  }, []);

  const completeIntro = useCallback(async () => {
    await completeOpenworkIntro();
  }, []);

  return { markRead, reset, completeIntro };
}
